/**
 * Derived "needs" — the answerable inputs a deployment of an arc template must be able to
 * supply values for — and the sealing check that validates an answer against them
 * (specs/arc-model-v2.md, EAINT-888 Phase 2).
 *
 * A need is a pure function of the template definition (plus, for key dates, the optionally
 * composing schema): the same definition always derives the same needs, independent of any
 * specific deployment. This module carries zero fastify/knex/authz imports — same constraint
 * as `archetype.ts` — so it stays a plain, cacheable, unit-testable function of its inputs.
 */

/** Where an answer for a need may originate. */
export type ArcNeedAnswerSite = 'sphere' | 'type';

export type ArcNeedKind = 'key_date' | 'starting_stage';

export interface ArcNeed {
  id: string;
  kind: ArcNeedKind;
  /** The key-date name (matches `ArcTemplateDefinition.keyDates`'s key). Only set for `kind: 'key_date'`. */
  key?: string;
  /** Sites permitted to answer this need. */
  answerableAt: ArcNeedAnswerSite[];
}

/** Builds a need's stable id. `starting_stage` has one need; `key_date` has one per declared key. */
export function needId(kind: ArcNeedKind, key?: string): string {
  if (kind === 'starting_stage') {
    return 'starting_stage';
  }
  if (!key) {
    throw new Error(`needId: 'key' is required for kind '${kind}'`);
  }
  return `key_date:${key}`;
}

/** Minimal shape this module needs from `ArcTemplateDefinition` — kept local, mirroring
 *  `archetype.ts`'s `ArchetypeCheckableDefinition`, so this package stays import-free. */
interface NeedsCheckableDefinition {
  keyDates?: Record<string, { expression?: string }>;
}

/**
 * Minimal shape of a MERGED schema (post `fetchAndMergeDefinitions`) this module needs to
 * decide whether a key-date's expression resolves to a property the schema composes via an
 * aspect — `aspect_system_name` is stamped only onto aspect-contributed properties
 * (`types/schema.d.ts` `BaseProperty.aspect_system_name`).
 */
interface NeedsCheckableSchema {
  properties?: Array<{ name: string; aspect_system_name?: string }>;
}

export interface DeriveArcNeedsOptions {
  /**
   * When supplied, a key-date need whose `expression` resolves to a property this schema
   * composes via an aspect gains `'type'` in `answerableAt` — an author of that schema type
   * can pre-answer the need for every sphere of that type, because the aspect's value can be
   * set at the type level. Absent `forSchema`, every key-date need is sphere-only.
   */
  forSchema?: NeedsCheckableSchema;
}

/**
 * Derives every need a deployment of `definition` must be able to answer: one `key_date` need
 * per declared `keyDates` entry, plus a `starting_stage` need every template carries (which
 * stage a deployment enters at). Order is declaration order for key dates, then
 * `starting_stage` last.
 */
export function deriveArcNeeds(definition: NeedsCheckableDefinition, opts: DeriveArcNeedsOptions = {}): ArcNeed[] {
  const needs: ArcNeed[] = [];

  for (const [key, keyDate] of Object.entries(definition.keyDates ?? {})) {
    const answerableAt: ArcNeedAnswerSite[] = ['sphere'];
    if (opts.forSchema && expressionHitsComposedAspect(keyDate.expression, opts.forSchema)) {
      answerableAt.push('type');
    }
    needs.push({ id: needId('key_date', key), kind: 'key_date', key, answerableAt });
  }

  needs.push({ id: needId('starting_stage'), kind: 'starting_stage', answerableAt: ['sphere'] });

  return needs;
}

/**
 * A key-date expression is type-answerable only when it reads the PARENT sphere's own
 * property — `parent.<property>` exactly, the two-segment grammar `resolveAnchorExpression`
 * accepts — and that property is one the composing schema (`forSchema`, the parent's type)
 * contributes via an aspect. Matching on the trailing segment alone would let
 * `sphere.<property>` (the arc vertex's own property, a different sphere with a different
 * schema) gain `'type'` whenever the parent type happened to compose a same-named aspect, so
 * `update-schema` would accept a type answer the engine never reads.
 */
function expressionHitsComposedAspect(expression: string | undefined, schema: NeedsCheckableSchema): boolean {
  if (!expression) {
    return false;
  }
  const segments = expression.split('.');
  if (segments.length !== 2 || segments[0] !== 'parent' || !segments[1]) {
    return false;
  }
  const propertyName = segments[1];
  return (schema.properties ?? []).some((property) => property.name === propertyName && !!property.aspect_system_name);
}

export interface ArcAnswerValidationResult {
  valid: boolean;
  error?: string;
  /** The need id that failed validation, when `valid` is false. */
  needId?: string;
}

/**
 * Validates that every answer in `answers` is permitted to be supplied at `site`, against the
 * need it targets. Rejects an answer keyed to an id that isn't a recognized need (unknown
 * needs are not silently accepted) and an answer supplied at a site the need doesn't list in
 * `answerableAt` (e.g. a sphere-level answer for a need that is `answerableAt: ['type']` only).
 * `undefined` values are treated as "not answered" and skipped.
 */
export function validateAnswers(
  needs: ArcNeed[],
  answers: Record<string, unknown> | undefined,
  site: ArcNeedAnswerSite,
): ArcAnswerValidationResult {
  for (const [id, value] of Object.entries(answers ?? {})) {
    if (value === undefined) {
      continue;
    }
    const need = needs.find((candidate) => candidate.id === id);
    if (!need) {
      return { valid: false, error: `'${id}' is not a recognized need for this arc template`, needId: id };
    }
    if (!need.answerableAt.includes(site)) {
      return {
        valid: false,
        error: `'${id}' cannot be answered at the '${site}' level (answerable at: ${need.answerableAt.join(', ')})`,
        needId: id,
      };
    }
  }
  return { valid: true };
}
